import React from 'react';
import { thousandsSeparator } from 'utils';
import GearExpertsPersistentSearchBanner from '~/components/GearExperts/Search/GearExpertsPersistentSearchBanner';
import ModelLanderHeader from '~/components/Search/ModelLanderHeader';
import PlaHeader from '~/components/Search/PlaHeader';
import SearchBreadcrumbs from '~/components/Search/SearchBreadcrumbs';
import SimpleCategoryPicker from '~/components/Search/SimpleCategoryPicker';
import useFacetFiltering from '~/contexts/facetFiltering/useFacetFiltering';
import useBrowserRouteQuery from '~/hooks/useBrowserRouteQuery';
import { RailsPageView } from '~/typings/services/rails/tile';

interface Props {
  pageView?: RailsPageView | null;
}

const SearchPageHeader: React.FC<Props> = ({ pageView }) => {
  const { category, filterState, lander, similarToItem, paging } =
    useFacetFiltering();
  const query = useBrowserRouteQuery();

  const hasItemNotFound = query.unavailable_item == '1';

  let container = true;
  let breadcrumbs = true;
  let content: React.ReactNode;

  const pageSuffix =
    paging?.page && paging.page > 1 ? ` - Page ${paging.page}` : '';

  if (lander) {
    const landerFeaturedCategoriesBlock = pageView?.blocks?.find(
      b => b.layout === 'lander-featured-categories',
    );

    if (lander.page_view_id && landerFeaturedCategoriesBlock) {
      breadcrumbs = false;
      container = false;
      content = null;
    } else if (lander?.featured_item) {
      content = <PlaHeader item={lander?.featured_item} />;
    } else if (lander.model) {
      content = <ModelLanderHeader />;
    } else if (similarToItem && query.stid === '1') {
      content = (
        <>
          <PlaHeader item={similarToItem} />

          <h1 className="mb-4">
            {thousandsSeparator(paging?.total_count)} Similar{' '}
            {similarToItem.categories?.[0]?.name || 'Items'}
            {pageSuffix}
          </h1>
        </>
      );
    } else {
      content = (
        <>
          <h1 className="mb-4">
            {lander.name}
            {pageSuffix}
          </h1>
          {lander.show_categories && (
            <SimpleCategoryPicker
              className="-mx-4 mb-8 md:mb-12"
              showImages={!lander.show_categories_no_images}
            />
          )}
        </>
      );
    }
  } else if (filterState.q) {
    content = (
      <h1>
        Searching for &ldquo;{filterState.q}&rdquo;
        {category ? <> in {category.name}</> : null}
        {pageSuffix}
      </h1>
    );
  } else if (category) {
    content = (
      <h1>
        Searching {category.name}
        {pageSuffix}
      </h1>
    );
  } else {
    content = <h1>Search{pageSuffix}</h1>;
  }

  return (
    <>
      {hasItemNotFound && (
        <div className="container my-4">
          <div className="rounded border px-4 py-3">
            <div className="mb-2 text-lg font-semibold">Item Not Found</div>
            <p>Sorry, the item you are looking for could not be found.</p>
          </div>
        </div>
      )}

      <GearExpertsPersistentSearchBanner />
      <div className={container ? 'container mb-0 mt-4' : ''}>
        {breadcrumbs && (
          <div className="mb-3 mt-4">
            <SearchBreadcrumbs />
          </div>
        )}
        {content}
      </div>
    </>
  );
};

export default SearchPageHeader;
